import { Link, useLoaderData, type LoaderFunctionArgs } from "react-router";
import { PageHeader } from "@/components/layout/page-header";
import { Badge, EmptyState, Panel, PanelHeader, type Tone } from "@/components/ui";
import { useDocumentTitle } from "@/lib/document-title";
import { formatCompactMoney, formatDate, formatStage } from "@/lib/format";
import type { Borrower, Case, CaseStage } from "@/lib/types";
import { getBorrower } from "@/services/borrowers";
import { listCasesForBorrower } from "@/services/cases";

const STAGE_TONE: Record<CaseStage, Tone> = {
  data_collection: "caution",
  underwriting: "accent",
  approval: "positive",
  approved: "positive",
  rejected: "negative",
  closed: "neutral",
};

interface BorrowerData {
  borrower: Borrower;
  cases: Case[];
}

export async function borrowerLoader({ params }: LoaderFunctionArgs): Promise<BorrowerData> {
  const borrowerId = params["borrowerId"] ?? "";
  const borrower = await getBorrower(borrowerId);
  // Handled by the root error boundary, which renders "not found" for a 404.
  if (!borrower) throw new Response("Not found", { status: 404 });

  return { borrower, cases: await listCasesForBorrower(borrowerId) };
}

/**
 * The borrower profile is the relationship view, not a workspace. It exists to
 * answer "have we seen this company before, and how did that go" — then send
 * the analyst back into a case, which is where work happens.
 */
export function BorrowerPage() {
  const { borrower, cases } = useLoaderData<BorrowerData>();
  useDocumentTitle(borrower.name);

  return (
    <>
      <PageHeader title={borrower.name} description={borrower.industry} />

      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <Panel>
          <dl className="grid grid-cols-2 divide-x divide-y divide-line sm:grid-cols-4 sm:divide-y-0">
            <Fact label="Relationship since" value={String(borrower.relationshipSince)} />
            <Fact label="Current risk" value={borrower.riskGrade} />
            <Fact label="Total exposure" value={formatCompactMoney(borrower.totalExposure)} />
            <Fact label="Employees" value={String(borrower.employees)} />
          </dl>
          <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">
            {borrower.headquarters}
          </p>
        </Panel>

        <Panel>
          <PanelHeader title="Cases" description={`${cases.length} on record`} />
          {cases.length === 0 ? (
            <EmptyState title="No cases" />
          ) : (
            <ul className="divide-y divide-line">
              {cases.map((entry) => (
                <li key={entry.id}>
                  <Link
                    to={`/cases/${entry.id}`}
                    className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-accent-soft/40"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-ink">{entry.purpose}</span>
                      <span className="numeric mt-0.5 block text-xs text-ink-muted">
                        {entry.reference} · opened {formatDate(entry.openedAt)}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="numeric text-sm font-medium">
                        {formatCompactMoney(entry.requestedAmount)}
                      </span>
                      <Badge tone={STAGE_TONE[entry.stage]}>{formatStage(entry.stage)}</Badge>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-3">
      <dt className="eyebrow">{label}</dt>
      <dd className="numeric mt-1 text-lg font-semibold text-ink">{value}</dd>
    </div>
  );
}
